import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  TextField, 
  Select, 
  MenuItem, 
  Button, 
  IconButton, 
  Divider,
  FormControl,
  InputLabel,
  Alert
} from "@mui/material";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { useLetterhead } from "../../hooks/useLetterhead.ts";

export default function CompanyInfoCard({ templateState, setTemplateState, errors = {} }) {
  const [selectedFieldType, setSelectedFieldType] = useState("");
  const [customFieldName, setCustomFieldName] = useState("");
  const {
    companyProfile,
    companyDocuments,
    companyAddress,
    isUni
  } = useLetterhead();

  const customFields = templateState.customFields || [];

  const handleChange = (key, value) => {
    setTemplateState((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddCustomField = () => {
    const keyName = selectedFieldType === "Custom" ? customFieldName.trim() : selectedFieldType;
    if (!keyName) return;

    if (customFields.some((f) => f.key.toLowerCase() === keyName.toLowerCase())) {
      alert("This field already exists.");
      return;
    }

    const newField = { id: Date.now().toString(), key: keyName, value: "" };

    setTemplateState((prev) => ({
      ...prev,
      customFields: [...(prev.customFields || []), newField],
      showCompanyFields: { ...prev.showCompanyFields, [keyName]: true },
      showFooterFields: { ...prev.showFooterFields, [keyName]: false },
    }));

    setSelectedFieldType("");
    setCustomFieldName("");
  };

  const handleRemoveCustomField = (id, keyName) => {
    setTemplateState((prev) => {
      const newFields = (prev.customFields || []).filter((f) => f.id !== id);
      const newShowFields = { ...prev.showCompanyFields };
      delete newShowFields[keyName];
      const newShowFooterFields = { ...prev.showFooterFields };

      return { ...prev, customFields: newFields, showCompanyFields: newShowFields };
    });
  };

  const handleCustomFieldValueChange = (id, val) => {
    setTemplateState((prev) => ({
      ...prev,
      customFields: (prev.customFields || []).map((f) =>
        f.id === id ? { ...f, value: val } : f
      ),
    }));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setTemplateState((prev) => {
      const newFields = [...(prev.customFields || [])];
      const temp = newFields[index - 1];
      newFields[index - 1] = newFields[index];
      newFields[index] = temp;
      return { ...prev, customFields: newFields };
    });
  };

  const handleMoveDown = (index) => {
    if (index === customFields.length - 1) return;
    setTemplateState((prev) => {
      const newFields = [...(prev.customFields || [])];
      const temp = newFields[index + 1];
      newFields[index + 1] = newFields[index];
      newFields[index] = temp;
      return { ...prev, customFields: newFields };
    });
  };

  return (
    <Card 
      sx={{ 
        bgcolor: 'white', 
        border: '1px solid', 
        borderColor: 'grey.200', 
        borderRadius: 3, 
        p: 0, 
        boxShadow: 1 
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'grey.900' 
            }}
          >
            {isUni ? "University Information" : "Business Information"}
          </Typography>

          <TextField
            fullWidth
            label={isUni ? "University Name" : "Business Name"}
            placeholder={isUni ? "Your university name" : "Your business name"}
            value={templateState.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !!companyProfile?.name
            }}
          />

          <TextField
            fullWidth
            label="Email"
            placeholder={isUni ? "contact@university.edu" : "contact@company.com"}
            value={templateState.companyEmail}
            onChange={(e) => handleChange("companyEmail", e.target.value)}
            error={!!errors.companyEmail}
            helperText={errors.companyEmail}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !!companyProfile?.email
            }}
          />

          <TextField
            fullWidth
            label="Phone"
            placeholder="+1 (555) 123-4567"
            value={templateState.companyPhone}
            onChange={(e) => handleChange("companyPhone", e.target.value)}
            error={!!errors.companyPhone}
            helperText={errors.companyPhone}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !!companyProfile?.contact_number
            }}
          />

          <TextField
            fullWidth
            label="Address"
            placeholder="123 Main St, City, State 12345"
            multiline
            rows={2}
            value={templateState.companyAddress}
            onChange={(e) => handleChange("companyAddress", e.target.value)}
            error={!!errors.companyAddress}
            helperText={errors.companyAddress}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !!companyAddress
            }}
          />

          <TextField
            fullWidth
            label={isUni ? "Registration Number" : "Registration/License Number"}
            placeholder={isUni ? "e.g., Reg No / AISHE Code" : "e.g., Reg No / AISHE Code"}
            value={templateState.cin}
            onChange={(e) => handleChange("cin", e.target.value)}
            error={!!errors.cin}
            helperText={errors.cin}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !isUni && Boolean(
                companyDocuments?.companyDetails?.[0]?.CIN_number &&
                companyDocuments?.companyDetails?.[0]?.CIN_number !== "null"
              )
            }}
          />

          <TextField
            fullWidth
            label="GST Number"
            placeholder="e.g., 27AABCT1234A1Z0"
            value={templateState.gst}
            onChange={(e) => handleChange("gst", e.target.value)}
            error={!!errors.gst}
            helperText={errors.gst}
            variant="outlined"
            size="medium"
            InputProps={{
              readOnly: !!companyProfile?.gst
            }}
          />

          {/* Render Dynamic Fields with Up/Down Controls */}
          {customFields.length > 0 && (
            <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'grey.800', 
                  mb: 2 
                }}
              >
                Additional Fields
              </Typography>

              {customFields.map((field, index) => (
                <Box 
                  key={field.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-end', 
                    gap: 2, 
                    mb: 2 
                  }}
                >
                  {/* Move Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.5,
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    p: 0.5,
                    flexShrink: 0,
                    '& .MuiIconButton-root': {
                      p: 0.5,
                      minWidth: 0,
                      width: 32,
                      height: 32
                    }
                  }}>
                    <IconButton
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      size="small"
                      sx={{
                        color: 'grey.600',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'primary.50'
                        },
                        '&.Mui-disabled': {
                          opacity: 0.3,
                          cursor: 'not-allowed'
                        }
                      }}
                      title="Move Up"
                    >
                      <ChevronUp size={16} strokeWidth={2.5} />
                    </IconButton>

                    <Divider sx={{ bgcolor: 'grey.200' }} />

                    <IconButton
                      onClick={() => handleMoveDown(index)}
                      disabled={index === customFields.length - 1}
                      size="small"
                      sx={{
                        color: 'grey.600',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'primary.50'
                        },
                        '&.Mui-disabled': {
                          opacity: 0.3,
                          cursor: 'not-allowed'
                        }
                      }}
                      title="Move Down"
                    >
                      <ChevronDown size={16} strokeWidth={2.5} />
                    </IconButton>
                  </Box>

                  {/* Field Input */}
                  <TextField
                    fullWidth
                    label={field.key}
                    placeholder={`Enter ${field.key}`}
                    value={field.value}
                    onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{ flex: 1, minWidth: 0 }}
                  />

                  {/* Remove Button */}
                  <IconButton
                    onClick={() => handleRemoveCustomField(field.id, field.key)}
                    sx={{
                      mb: 1,
                      flexShrink: 0,
                      color: 'error.main',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      bgcolor: 'white',
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'error.50',
                        borderColor: 'error.200'
                      },
                      width: 40,
                      height: 40
                    }}
                    title="Remove Field"
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Add New Field Controls */}
          <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                color: 'grey.700', 
                mb: 2 
              }}
            >
              Add More Details
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <Select
                  value={selectedFieldType}
                  onChange={(e) => {
                    setSelectedFieldType(e.target.value);
                    if (e.target.value !== "Custom") setCustomFieldName("");
                  }}
                  displayEmpty
                  sx={{
                    height: 44,
                    bgcolor: 'white',
                    '& .MuiSelect-select': {
                      py: 1.5,
                      px: 2
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    Select field to add...
                  </MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                  <MenuItem value="Branch Office">Branch Office</MenuItem>
                  <MenuItem value="Custom">Custom Field...</MenuItem>
                </Select>
              </FormControl>

              {selectedFieldType === "Custom" && (
                <TextField
                  placeholder="Enter Field Name"
                  value={customFieldName}
                  onChange={(e) => setCustomFieldName(e.target.value)}
                  variant="outlined"
                  size="medium"
                  sx={{ flex: 1 }}
                  InputProps={{
                    sx: { height: 44 }
                  }}
                />
              )}

              <Button
                onClick={handleAddCustomField}
                disabled={!selectedFieldType || (selectedFieldType === "Custom" && !customFieldName.trim())}
                variant="contained"
                startIcon={<Plus size={16} />}
                sx={{
                  height: 44,
                  flexShrink: 0,
                  bgcolor: 'grey.900',
                  '&:hover': {
                    bgcolor: 'grey.800'
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.300',
                    cursor: 'not-allowed'
                  },
                  px: 4
                }}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}